import { WebHead } from '../components';
import { type Href, useRouter } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import {
  Linking,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import { trpc } from '../lib/trpc';
import { trackSupportPageViewed } from '../lib/product-events';
import { getLegalUrl, openExternalUrl } from '../lib/utils';

const PRIVACY_URL = getLegalUrl('privacy');
const SECURITY_URL = getLegalUrl('security');
const TERMS_URL = getLegalUrl('terms');
const HOME_ROUTE = '/' as Href;
const LOGIN_ROUTE = '/(auth)/login' as Href;
const AI_CONNECTORS_ROUTE = '/ai-connectors' as Href;

const FAQS = [
  {
    question: 'How do I connect my accounts?',
    answer:
      'Per Diem uses secure financial data connections for bank and brokerage linking. Inside the app, go to the accounts flow and choose the institution you want to connect.',
  },
  {
    question: 'How is my FIRE projection calculated?',
    answer:
      'Your plan blends current balances, spending, income, and scenario inputs to simulate retirement readiness and probability over time instead of relying on a single back-of-the-envelope number.',
  },
  {
    question: 'Can I remove a connection later?',
    answer:
      'Yes. You can disconnect a linked institution from the app settings. Historical records may remain for continuity, but new sync access is removed immediately.',
  },
  {
    question: 'Is my financial data secure?',
    answer:
      'Per Diem is designed around read-only financial connections, encrypted storage for sensitive provider data, and hosted authentication rather than collecting your raw bank credentials directly.',
  },
  {
    question: 'What if I need help with billing or access?',
    answer:
      'Use the contact form below. Include the email on your account and a short description of the issue so support can route it quickly.',
  },
];

export default function SupportPage() {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const isTwoColumn = width >= 960;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number>(0);

  useEffect(() => {
    trackSupportPageViewed({ route: '/support' });
  }, []);

  const submitMutation = trpc.support.submit.useMutation({
    onSuccess: () => {
      setSubmitted(true);
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
      setError(null);
    },
    onError: (err) => {
      setError(err.message || 'Something went wrong. Please try again.');
    },
  });

  const isValid = useMemo(
    () => !!name.trim() && !!email.trim() && !!subject.trim() && !!message.trim(),
    [email, message, name, subject],
  );

  const submit = () => {
    if (!isValid) {
      setError('Please complete all fields before submitting.');
      return;
    }

    setError(null);
    submitMutation.mutate({
      name: name.trim(),
      email: email.trim(),
      subject: subject.trim(),
      message: message.trim(),
    });
  };

  return (
    <>
      <WebHead>
        <title>Per Diem Support</title>
        <meta
          name="description"
          content="Contact Per Diem support, review common account questions, and access support, privacy, and terms resources."
        />
      </WebHead>
      <ScrollView className="flex-1 bg-stone-950" contentContainerStyle={{ paddingBottom: 48 }}>
        <View className="absolute left-[-90] top-[-40] h-72 w-72 rounded-full bg-emerald-500/20" />
        <View className="absolute right-[-80] top-24 h-72 w-72 rounded-full bg-orange-400/10" />

        <View className="mx-auto w-full max-w-6xl px-6 pb-10 pt-6">
          <View className="mb-10 flex-row items-center justify-between">
            <Pressable onPress={() => router.push(HOME_ROUTE)}>
              <Text className="text-sm font-semibold uppercase tracking-[0.28em] text-emerald-300">
                Per Diem
              </Text>
            </Pressable>
            <View className="flex-row gap-3">
              <Pressable
                onPress={() => router.push(LOGIN_ROUTE)}
                className="rounded-full border border-stone-700 px-4 py-2"
              >
                <Text className="text-sm font-semibold text-stone-100">Sign In</Text>
              </Pressable>
            </View>
          </View>

          <View className="rounded-[32px] border border-stone-800 bg-stone-900 px-6 py-8">
            <Text className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-300">
              Support & Help Center
            </Text>
            <Text className="mt-3 max-w-3xl text-4xl font-semibold leading-[48px] text-stone-50">
              Help for account access, billing, and the path to financial independence.
            </Text>
            <Text className="mt-4 max-w-2xl text-base leading-7 text-stone-300">
              This page is public and safe to share with App Store review. Use the contact form
              below to reach our support team.
            </Text>
          </View>

          <View
            className="mt-8 gap-4"
            style={{ flexDirection: isTwoColumn ? 'row' : 'column', alignItems: 'stretch' }}
          >
            <View className="rounded-[28px] border border-stone-800 bg-stone-900 p-6" style={{ flex: 1 }}>
              <Text className="text-2xl font-semibold text-stone-50">Contact support</Text>
              <Text className="mt-2 text-base leading-7 text-stone-300">
                Fill out the form below and our team will get back to you. Include the account
                email you use in Per Diem and enough detail for us to help.
              </Text>

              {submitted ? (
                <View className="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-6">
                  <Text className="text-center text-lg font-semibold text-emerald-300">
                    Request received
                  </Text>
                  <Text className="mt-2 text-center text-sm leading-6 text-stone-300">
                    We got your message and will respond within 24 hours on business days.
                  </Text>
                  <Pressable
                    onPress={() => setSubmitted(false)}
                    className="mt-4 self-center rounded-2xl border border-stone-700 px-5 py-3"
                  >
                    <Text className="text-sm font-semibold text-stone-100">Send another request</Text>
                  </Pressable>
                </View>
              ) : (
                <>
                  <View className="mt-6 gap-3">
                    <TextInput
                      value={name}
                      onChangeText={(value) => {
                        setName(value);
                        if (error) setError(null);
                      }}
                      placeholder="Your name"
                      placeholderTextColor="#78716c"
                      className="rounded-2xl border border-stone-700 bg-stone-950 px-4 py-4 text-base text-stone-50"
                    />
                    <TextInput
                      value={email}
                      onChangeText={(value) => {
                        setEmail(value);
                        if (error) setError(null);
                      }}
                      placeholder="Your email"
                      placeholderTextColor="#78716c"
                      keyboardType="email-address"
                      autoCapitalize="none"
                      className="rounded-2xl border border-stone-700 bg-stone-950 px-4 py-4 text-base text-stone-50"
                    />
                    <TextInput
                      value={subject}
                      onChangeText={(value) => {
                        setSubject(value);
                        if (error) setError(null);
                      }}
                      placeholder="Subject"
                      placeholderTextColor="#78716c"
                      className="rounded-2xl border border-stone-700 bg-stone-950 px-4 py-4 text-base text-stone-50"
                    />
                    <TextInput
                      value={message}
                      onChangeText={(value) => {
                        setMessage(value);
                        if (error) setError(null);
                      }}
                      placeholder="What happened, and what did you expect instead?"
                      placeholderTextColor="#78716c"
                      multiline
                      numberOfLines={6}
                      textAlignVertical="top"
                      className="min-h-[160px] rounded-2xl border border-stone-700 bg-stone-950 px-4 py-4 text-base text-stone-50"
                    />
                  </View>

                  {error ? (
                    <View className="mt-4 rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3">
                      <Text className="text-sm text-red-200">{error}</Text>
                    </View>
                  ) : null}

                  <Pressable
                    onPress={submit}
                    disabled={submitMutation.isPending}
                    className="mt-5 rounded-2xl bg-emerald-400 px-5 py-4"
                    style={submitMutation.isPending ? { opacity: 0.6 } : undefined}
                  >
                    <Text className="text-center text-base font-semibold text-stone-950">
                      {submitMutation.isPending ? 'Sending...' : 'Submit request'}
                    </Text>
                  </Pressable>
                </>
              )}

              <View className="mt-6 gap-3 rounded-[24px] border border-stone-800 bg-stone-950 p-4">
                <Text className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-400">
                  Response time
                </Text>
                <Text className="text-sm leading-6 text-stone-400">
                  Typical response window: within 24 hours on business days.
                </Text>
              </View>
            </View>

            <View className="gap-4" style={{ flex: 1 }}>
              <View className="rounded-[28px] border border-stone-800 bg-stone-900 p-6">
                <Text className="text-2xl font-semibold text-stone-50">Common questions</Text>
                <Text className="mt-2 text-base leading-7 text-stone-300">
                  A few of the things people usually need before reaching out.
                </Text>

                <View className="mt-5 gap-3">
                  {FAQS.map((item, index) => {
                    const expanded = expandedIndex === index;
                    return (
                      <View
                        key={item.question}
                        className="overflow-hidden rounded-2xl border border-stone-800 bg-stone-950"
                      >
                        <Pressable
                          onPress={() => setExpandedIndex(expanded ? -1 : index)}
                          className="px-4 py-4"
                        >
                          <Text className="text-base font-semibold text-stone-100">{item.question}</Text>
                        </Pressable>
                        {expanded ? (
                          <View className="border-t border-stone-800 px-4 py-4">
                            <Text className="text-sm leading-6 text-stone-400">{item.answer}</Text>
                          </View>
                        ) : null}
                      </View>
                    );
                  })}
                </View>
              </View>

              <View className="rounded-[28px] border border-stone-800 bg-stone-900 p-6">
                <Text className="text-2xl font-semibold text-stone-50">Policy links</Text>
                <Text className="mt-2 text-base leading-7 text-stone-300">
                  Public references for App Store review, user support, and account questions.
                </Text>

                <View className="mt-5 gap-3">
                  <Pressable
                    onPress={() => openExternalUrl(PRIVACY_URL)}
                    className="rounded-2xl border border-stone-800 bg-stone-950 px-4 py-4"
                  >
                    <Text className="text-sm font-semibold uppercase tracking-[0.18em] text-stone-400">
                      Privacy
                    </Text>
                    <Text className="mt-2 text-base font-semibold text-stone-100">{PRIVACY_URL}</Text>
                  </Pressable>
                  <Pressable
                    onPress={() => openExternalUrl(SECURITY_URL)}
                    className="rounded-2xl border border-stone-800 bg-stone-950 px-4 py-4"
                  >
                    <Text className="text-sm font-semibold uppercase tracking-[0.18em] text-stone-400">
                      How we keep your data safe
                    </Text>
                    <Text className="mt-2 text-base font-semibold text-stone-100">{SECURITY_URL}</Text>
                  </Pressable>
                  <Pressable
                    onPress={() => openExternalUrl(TERMS_URL)}
                    className="rounded-2xl border border-stone-800 bg-stone-950 px-4 py-4"
                  >
                    <Text className="text-sm font-semibold uppercase tracking-[0.18em] text-stone-400">
                      Terms
                    </Text>
                    <Text className="mt-2 text-base font-semibold text-stone-100">{TERMS_URL}</Text>
                  </Pressable>
                  <Pressable
                    onPress={() => router.push(AI_CONNECTORS_ROUTE)}
                    className="rounded-2xl border border-stone-800 bg-stone-950 px-4 py-4"
                  >
                    <Text className="text-sm font-semibold uppercase tracking-[0.18em] text-stone-400">
                      AI connector docs
                    </Text>
                    <Text className="mt-2 text-base font-semibold text-stone-100">
                      perdiem.app/ai-connectors
                    </Text>
                  </Pressable>
                </View>
              </View>
            </View>
          </View>
        </View>
      </ScrollView>
    </>
  );
}
